import { useDispatch } from "react-redux";
import useSelector from "src/utils/useSelector";
import { useEffect } from "react";
import { carryOverFromConversations, getTranscriptFailure } from "src/components/Chat/redux/actions";
import { logError } from "src/utils/logger";

export default function useChatCarryOver() {
    const dispatch = useDispatch();

    const isAppOpen = useSelector((state) => state.onlineShopper.isAppOpen);
    const token = useSelector((state) => state.messages?.conversations_config?.token);
    const channel = useSelector((state) => state.messages?.conversations_config?.channel);
    const enabled = useSelector((state) => state.account.settings?.electric?.message_center_enabled);
    const missingConversationsConfig = window.DIConversationsConfig === undefined;

    useEffect(() => {
        let tokenFromStorage: string | undefined | null;
        let channelFromStorage: string | undefined | null;

        try {
            tokenFromStorage = JSON.parse(
                window.localStorage.getItem("@conversations/embed:viewer.beta-1") ?? "{}"
            ).token;
            channelFromStorage = JSON.parse(
                window.localStorage.getItem(
                    `@conversations/embed:modules:${window?.DIConversationsConfig?.guid}.beta-1`
                ) ?? "{}"
            ).messenger?.usingConversation;
        } catch (e) {
            logError(e);
        }

        if (
            isAppOpen &&
            enabled &&
            channelFromStorage &&
            tokenFromStorage &&
            (!channel || channelFromStorage !== channel) &&
            (!token || tokenFromStorage !== token) &&
            !missingConversationsConfig
        ) {
            dispatch(carryOverFromConversations({ token: tokenFromStorage, channel: channelFromStorage }));
        }
    }, [isAppOpen, enabled, token, channel, missingConversationsConfig]);

    useEffect(() => {
        if (!isAppOpen && enabled && channel && token && !missingConversationsConfig) {
            try {
                // Here through line 67 is solving for an edge case and isn't really needed due to reducer.ts line 312.
                // But incase we ever move the spot we refetch the transcript from the top level App component this ensures
                // We won't carry a chat back over to conversations embed that they have already ended.
                // (Say you chat in conv, go to garage chat, go back to conv, end chat, go back to garage *not chat*,
                // when we close, don't send the chat back. Like I said, edge case)
                const hadChannel = JSON.parse(
                    window.localStorage.getItem(
                        `@conversations/embed:conversations:${window?.DIConversationsConfig?.guid}.beta-1`
                    ) ?? "[]"
                ).some((conv: any) => conv?.channelName === channel);

                const channelFromStorage = JSON.parse(
                    window.localStorage.getItem(
                        `@conversations/embed:modules:${window?.DIConversationsConfig?.guid}.beta-1`
                    ) ?? "{}"
                ).messenger?.usingConversation;

                if (!channelFromStorage && hadChannel) {
                    dispatch(getTranscriptFailure());
                    return;
                }

                const viewerObject = JSON.parse(
                    window.localStorage.getItem("@conversations/embed:viewer.beta-1") ?? "{}"
                );
                const modulesObject = JSON.parse(
                    window.localStorage.getItem(
                        `@conversations/embed:modules:${window?.DIConversationsConfig?.guid}.beta-1`
                    ) ?? "{}"
                );

                viewerObject.token = token;
                modulesObject.messenger.usingConversation = channel;
                modulesObject.proactive.lastDismissed = Date.now();

                window.localStorage.setItem("@conversations/embed:viewer.beta-1", JSON.stringify(viewerObject));
                window.localStorage.setItem(
                    `@conversations/embed:modules:${window?.DIConversationsConfig?.guid}.beta-1`,
                    JSON.stringify(modulesObject)
                );
                window.localStorage.removeItem(
                    `@conversations/embed:conversations:${window?.DIConversationsConfig?.guid}.beta-1`
                );

                // @ts-ignore
                window?.DIConversations?.start({
                    ...{
                        appID: "di-conversations-embed",
                        pusher: {
                            appCluster: "mt1",
                            appKey: window.DIConversationsConfig?.raw.pusherKey,
                            authEndpoint: `${
                                window.DIConversationsConfig?.endpoints.conversationsPHP as string
                            }/pusher/auth/visitor`,
                        },
                    },
                    ...window.DIConversationsConfig,
                });
            } catch (e) {
                logError(e);
            }
        }
    }, [isAppOpen, enabled, token, channel, missingConversationsConfig]);
}
